import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const LineChart = ({ title, data, labels, color = '#10B981' }) => {
  // Memoize chart data to prevent re-render loops
  const chartData = useMemo(() => ({
    labels: labels,
    datasets: [
      {
        label: title,
        data: data,
        borderColor: color,
        backgroundColor: `${color}20`,
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
      },
    ],
  }), [labels, data, title, color]);

  // Memoize chart options with aggressive resize prevention
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 200,
    // Disable animations to prevent resize thrashing
    animation: false,
    // Explicitly disable resize animations
    transitions: {
      resize: {
        animation: {
          duration: 0
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: '#0F172A',
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          family: 'Inter',
          size: 13,
          weight: '600',
        },
        bodyFont: {
          family: 'Inter',
          size: 14,
          weight: '500',
        },
        callbacks: {
          label: function(context) {
            return `$${context.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 12,
            weight: '500',
          },
          color: '#6B7280',
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 20,
        },
      },
      y: {
        grid: {
          color: '#F1F5F9',
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          font: {
            family: 'Inter',
            size: 12,
            weight: '500',
          },
          color: '#6B7280',
          callback: function(value) {
            return '$' + value.toLocaleString('en-US');
          },
        },
      },
    },
  }), []);

  return (
    <div data-testid="line-chart" className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 p-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
      {/* Strict fixed height container to prevent any resize loop */}
      <div className="relative w-full" style={{ height: '360px', minHeight: '360px', maxHeight: '360px' }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default React.memo(LineChart);